/*
 * Module for tracking session state, and handling core login / logout functionality
 */

import { reactive, ref } from "vue";
import { STATIC_CONFIG } from "./config";
import axios from "axios";
import { generateRandomId } from "./utils";
import { Citizen, Feature, Partner } from "@minicity/core";

export interface Session {
    id: string;
    networkId: string;
    partnerId: string;
    
    user: Citizen.Citizen;
    userPreferences: any;
    partnerSettings: Partner.AdminSettings;
}

interface SessionState {
    session?: Session
}

export const sessionState = reactive<SessionState>({
    session: undefined
});

// Engagement ID is used for analytics tracking
export const engagementId = ref<string>(generateRandomId());
export const startNewEngagement = () => {
    engagementId.value = generateRandomId();
}

export const getAuthHeader = () => {
    return sessionState.session ? `Bearer ${sessionState.session.id}` : null;
}

// Attempts to resume any old session from info remaining in localStorage
export const tryToResumeSession = async (id?: string): Promise<Session> => {
    let sessionIdToResume;
    if (id) {
        sessionIdToResume = id;
    } else {
        if (!localStorage["session"]) throw ("No session to resume");

        const oldSession = JSON.parse(localStorage["session"]);
        sessionIdToResume = oldSession.id;
    }   

    console.log("Attempting to resume session");

    const session = await axios.get(`${STATIC_CONFIG.API_ROOT}/portal/citizen/sessions/${sessionIdToResume}`)
        .then(
            resp => { return resp.data; },
            err => {
                delete localStorage["session"];
                throw err;
            }
        );

    storeSession(session);
    return session;
}

export const login = async (username: string, password: string): Promise<Session> => {
    const session = await axios.post(
        `${STATIC_CONFIG.API_ROOT}/portal/citizen/login`,
        {username:username, password:password},
        {headers: {"Content-Type": "application/json"}}
    ).then(resp => { return resp.data; }, err => { throw err; });

    storeSession(session);
    return session;
}

export const initiatePasswordReset = async (username: string): Promise<void> => {
    await axios.post(
        `${STATIC_CONFIG.API_ROOT}/portal/citizen/passwordReset`,
        {username:username},
        {headers: {"Content-Type": "application/json"}}
    );
}

export const validatePasswordReset = async (username: string, token: string): Promise<boolean> => {
    return await axios.post(
        `${STATIC_CONFIG.API_ROOT}/portal/citizen/passwordReset:validate`,
        {username:username, token:token},
        {headers: {"Content-Type": "application/json"}}
    )
    .then(() => true)
    .catch((err) => {
        if (err.response.status == 404) {
            return false; // 404 indicates invalid token
        } else {
            throw err;
        }
    });
}

export const applyPasswordReset = async (username: string, token: string, password: string): Promise<Session> => {
    await axios.post(
        `${STATIC_CONFIG.API_ROOT}/portal/citizen/passwordReset:apply`,
        {username:username, password:password, token:token},
        {headers: {"Content-Type": "application/json"}}
    );

    return login(username, password);
}

// Store session info in local storage
export const storeSession = (session: Session) => {
    console.log("Storing session");

    sessionState.session = session;
    localStorage["session"] = JSON.stringify(session);

    startNewEngagement();
}

export const endSession = () => {
    if (sessionState.session) {
        console.log("Ending session");
        axios.delete(
            `${STATIC_CONFIG.API_ROOT}/sessions/${sessionState.session.id}`
        );
    }

    // Clear all session info from local storage
    console.log("Clearing session data");
    sessionState.session = undefined;
    delete localStorage["session"];
};

// Determines whether the specified module / feature is enabled for this user
export const isModuleEnabled = (id: string): boolean => {
    if (!sessionState.session) return false;
    return sessionState.session.partnerSettings.enabledModules[id] == Feature.Status.ENABLED;
}

export const canSeeFeature = (id: string): boolean => {
    if (!sessionState.session) return false;
    
    switch (id) {
        case "partnerOffers":
            return true;
        default:
            return false;
    }
}

export const chromeless = ref<boolean>(false);